import { MappingDimensionKey } from '@pubstack/common/src/bid-params-mapping'
import { BidderCatalog, marketplaceAliasSuffix, Platform } from '@pubstack/common/src/bidderCatalog'
import {
  BidderSeatType,
  buildSparkForPubBidderSeatName,
  PBSTCK_BIDDER_SEAT_CODE,
  PBSTCK_BIDDER_SEAT_NAME,
  SERVER_SIDE_BIDDER_SEAT_NAME_SUFFIX,
  SPARK_DEMAND_BIDDER_SEAT_NAME_SUFFIX,
} from '@pubstack/common/src/bidder-seat'
import { UpgradeOffer } from '@pubstack/common/src/upgrade'
import { CardIntegration } from '~/components/catalog/ModuleCard'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'

/**
 * How a seat is set up: carries both the seat type and the platform; the product (hence the
 * endpoint) follows from the type via `productOfBidderSeatType`.
 */
export type SeatSetupMethod = 'client-side' | 'marketplace' | 'adm-server-side' | 'server-side' | 'spark-for-pubs' | 'spark-demand'

export const SEAT_SPEC_BY_METHOD: Record<SeatSetupMethod, { type: BidderSeatType; platform: Platform }> = {
  'client-side': { type: 'adm', platform: 'client' },
  marketplace: { type: 'adm', platform: 'client' },
  'adm-server-side': { type: 'adm', platform: 'server' },
  'server-side': { type: 'node', platform: 'server' },
  'spark-for-pubs': { type: 'spark_for_pubs', platform: 'server' },
  'spark-demand': { type: 'spark_demand', platform: 'server' },
}

/**
 * Mapped by placement rather than by ad unit, so they skip the mapping-type question entirely. Both are
 * Spark seats: the Spark team owns the placement IDs on one side or the other.
 */
export const isPlacementMappedMethod = (method: SeatSetupMethod): boolean => method === 'spark-for-pubs' || method === 'spark-demand'

const NAME_SUFFIX_BY_METHOD: Record<SeatSetupMethod, string> = {
  'client-side': '',
  marketplace: marketplaceAliasSuffix,
  'adm-server-side': SERVER_SIDE_BIDDER_SEAT_NAME_SUFFIX,
  'server-side': SERVER_SIDE_BIDDER_SEAT_NAME_SUFFIX,
  'spark-for-pubs': '',
  'spark-demand': SPARK_DEMAND_BIDDER_SEAT_NAME_SUFFIX,
}

/**
 * The pre-filled (freely editable) adapter name, derived from the bidder code so it already matches
 * `BIDDER_SEAT_NAME_REGEX`. The two server-side methods share a suffix and collide by default:
 * names are unique per scope across every type, and the collision surfaces inline on the name field.
 */
export const buildSeatName = (method: SeatSetupMethod, bidderCode: string): string =>
  bidderCode === PBSTCK_BIDDER_SEAT_CODE
    ? // Spark is the one adapter whose seat the backend used to create itself, always as `Spark`. It is now
      // created from here instead, so keep the name it has always had on every existing scope rather than
      // deriving `pbstck-bidder-s2s`. Nothing keys on it — the lookups moved to the adapter code — but a
      // Spark seat called `Spark` is what support and the Spark team expect to see.
      PBSTCK_BIDDER_SEAT_NAME
    : method === 'spark-for-pubs'
      ? buildSparkForPubBidderSeatName(bidderCode)
      : `${bidderCode}${NAME_SUFFIX_BY_METHOD[method]}`

export type SeatSetupOption = {
  value: SeatSetupMethod
  title: string
  description: string
  /** Rendered inline on the card, not as a tooltip: disabled controls swallow hover. */
  disabledReason?: string
  /** Set where `disabledReason` means "not yet": the card then pitches — empty pitches too, see `MethodCard`. */
  upgradeOffers?: UpgradeOffer[]
  isSuggested?: boolean
  /** Discouraged but still selectable — the card is warning-tinted, not disabled. */
  isDeprecated?: boolean
  /** Unavailable because the seat exists already, not because something is missing: shown as done rather than blocked. */
  isAlreadyCreated?: boolean
  /** Pubstack-internal: offered only to staff, and marked so they never mistake it for something a customer sees. */
  isInternal?: boolean
  trackingLabel: TrackingLabel
}

const TRACKING_PREFIX = 'pbs-create-seat-stepper-setup-method'

export const SUBSCRIBE_TO_ONE = 'Available with the One offer. Contact us to subscribe.'
export const SUBSCRIBE_TO_NODE = 'Available with the Node offer. Contact us to subscribe.'
const CONTACT_FOR_SPARK = 'Contact us to enable Spark for Pubs on your account.'
export const CONTACT_FOR_SPARK_DEMAND = 'Contact us to enable Spark on your account.'
const SPARK_SEAT_ALREADY_CREATED = 'Seat already created'

export type SeatSetupContext = {
  bidder: Pick<BidderCatalog, 'code' | 'displayName' | 'disableAliasing' | 'isServerSideCompatible' | 'isSparkForPubCompatible'>
  /** Ad Management — `adstack`. Carries One's client-side seat. */
  hasAdManagement: boolean
  /** The Node option — `pbsStandalone`. Carries both server-side seats, and nothing else. */
  hasNode: boolean
  /**
   * Onboarded on Spark for Pubs **and** allowed to act on it — the whole gate, with no offer behind it.
   * A Pubstack user has every feature synthesised for the scope they are in, so onboarding alone opens
   * the seat to staff; a customer's own users need the `sparkForPub` access feature granted on top.
   */
  canCreateSparkForPubSeat: boolean
  sparkForPubAlreadyExists: boolean
  /** Onboarded on Spark (`onboardedFeatures.pbstckBidder`) and visible to this viewer, see `sparkStaffState().viewerOnboarded`. */
  isSparkOnboarded: boolean
  /** Whether the scope already has its one Spark seat. `onboardScope` creates it, so usually yes. */
  pbstckBidderSeatAlreadyExists: boolean
  /** A Pubstack user with the `marketplace` feature, on a bidder activated in the scope's marketplace. */
  canCreateMarketplaceSeat?: boolean
}

/**
 * The seat types a bidder can be set up with, in a fixed order. Availability is scope-level, never
 * page-level: both the catalog cards and the creation flyout read this list, so neither can promise
 * a seat the other blocks.
 *
 * The two axes are handled differently on purpose: what the **bidder** supports decides whether an
 * option exists at all (an unsupported integration is left out, not greyed out); what the **scope**
 * has decides whether it is selectable (shown disabled, because subscribing is something the user
 * can act on).
 */
export const seatSetupOptions = ({
  bidder,
  hasAdManagement,
  hasNode,
  canCreateSparkForPubSeat,
  sparkForPubAlreadyExists,
  isSparkOnboarded,
  pbstckBidderSeatAlreadyExists,
  canCreateMarketplaceSeat = false,
}: SeatSetupContext): SeatSetupOption[] => {
  // Spark is Pubstack's own demand rather than an adapter you integrate, so its card answers to none of
  // the rules below. It is server-side only — a One-only scope can still create it, because the bid
  // requests leave Prebid Server whatever the scope runs client-side — and what gates it is the Spark
  // team having onboarded the scope, never an offer the customer could go and buy.
  if (bidder.code === PBSTCK_BIDDER_SEAT_CODE) {
    // Existence first, for the same reason as Spark for Pubs below. `CreateSeatStepper` assumes the seat
    // exists on a `sparkForNetwork` scope, whose listings hide it, and such a scope need not be onboarded:
    // asking onboarding first answered "contact us" for a seat the caller had just said was there.
    const sparkDemandReason = pbstckBidderSeatAlreadyExists ? SPARK_SEAT_ALREADY_CREATED : !isSparkOnboarded ? CONTACT_FOR_SPARK_DEMAND : undefined
    return [
      {
        value: 'server-side',
        title: 'Server-side',
        description: 'Pubstack’s own demand, served from Prebid Server. The Spark team handles the mappings and the bid params.',
        disabledReason: sparkDemandReason,
        isSuggested: !sparkDemandReason,
        isAlreadyCreated: sparkDemandReason === SPARK_SEAT_ALREADY_CREATED,
        // Nothing to buy — the Spark team switches it on — so the card asks rather than sells, with the
        // same crown the rest wear. An existing seat is not an ask, and pitches nothing.
        upgradeOffers: sparkDemandReason === CONTACT_FOR_SPARK_DEMAND ? [] : undefined,
        trackingLabel: `${TRACKING_PREFIX}-server-side-set`,
      },
    ]
  }

  const options: SeatSetupOption[] = []

  if (!bidder.disableAliasing) {
    options.push({
      value: 'client-side',
      title: 'Client-side',
      description: 'Bid requests are sent from the browser. Map ad units and bid params yourself.',
      disabledReason: hasAdManagement ? undefined : SUBSCRIBE_TO_ONE,
      // Not bought rather than not allowed, so the card sells the offer instead of padlocking the choice.
      upgradeOffers: hasAdManagement ? undefined : [OFFER_BY_METHOD['client-side']],
      trackingLabel: `${TRACKING_PREFIX}-client-side-set`,
    })
  }

  if (!bidder.disableAliasing && canCreateMarketplaceSeat) {
    options.push({
      value: 'marketplace',
      title: 'Marketplace',
      description: 'A client-side seat Pubstack sells on the publisher’s behalf. Its name ends with -pbstck.',
      disabledReason: hasAdManagement ? undefined : SUBSCRIBE_TO_ONE,
      upgradeOffers: hasAdManagement ? undefined : [OFFER_BY_METHOD.marketplace],
      isInternal: true,
      trackingLabel: `${TRACKING_PREFIX}-marketplace-set`,
    })
  }

  if (bidder.isServerSideCompatible) {
    options.push({
      value: 'server-side',
      title: 'Server-side',
      description: 'Bid requests are sent from Prebid Server. Map ad units and bid params yourself.',
      disabledReason: hasNode ? undefined : SUBSCRIBE_TO_NODE,
      upgradeOffers: hasNode ? undefined : [OFFER_BY_METHOD['server-side']],
      trackingLabel: `${TRACKING_PREFIX}-server-side-set`,
    })
  }

  if (bidder.isSparkForPubCompatible) {
    // Existence first, then the one thing that gates the seat. A seat that is already there is not an
    // upsell: asked the other way round, a scope missing the Spark feature was pitched an upgrade for a
    // seat it had already been given — a crown offering to sell what it owns. Kept as a card rather than
    // dropped so Spark does not silently vanish from the bidder it was set up on.
    //
    // Node is deliberately not asked, for the same reason Spark Demand above does not ask it: this is
    // Pubstack's own demand served from Prebid Server, so it needs no Prebid Server of the customer's,
    // and what switches it on is the Spark team having onboarded the scope — never an offer anyone could
    // go and buy. Requiring it blocked exactly the scopes the feature is provisioned for: a One customer
    // the Spark team had already onboarded was told to subscribe to Node for a seat Node does not sell.
    //
    // `canCreateSparkForPubSeat` carries both halves of the rule on its own: onboarding opens the seat to
    // Pubstack staff, who have every feature synthesised for the scope they are in, and the customer's own
    // users reach it once granted the `sparkForPub` access feature on top.
    const sparkForPubReason = sparkForPubAlreadyExists ? SPARK_SEAT_ALREADY_CREATED : !canCreateSparkForPubSeat ? CONTACT_FOR_SPARK : undefined
    options.push({
      value: 'spark-for-pubs',
      title: 'Spark for Pubs',
      description: 'Activate only the ad units you want to map, the Spark team handles placement IDs and keeps mappings updated.',
      disabledReason: sparkForPubReason,
      isSuggested: !sparkForPubReason,
      isAlreadyCreated: sparkForPubReason === SPARK_SEAT_ALREADY_CREATED,
      /* An ask, never a sale: there is nothing to buy, so the empty list is what wears the crown — and
         only an existing seat pitches nothing at all. */
      upgradeOffers: sparkForPubReason === CONTACT_FOR_SPARK ? [] : undefined,
      trackingLabel: `${TRACKING_PREFIX}-spark-for-pubs-set`,
    })
  }

  // Last, and after Spark: every option above it is one to pick, and this is the one to pick only if
  // something already in place demands it. It used to sit directly under the seat that replaces it,
  // which still put a legacy integration ahead of a current one.
  //
  // Hidden rather than shown disabled, unlike every other gated option here: a deprecated seat is not an
  // offer, so dangling it in front of a scope that cannot create it advertises the legacy integration as
  // a reason to buy something — when the seats above it are what they would actually get.
  //
  // One *and* Node, not Node alone. The seat is stored as an ADM one and served through Prebid Server,
  // so it is the shape a scope ends up with only by having had both; a Node-only scope has no ADM
  // inventory for it to sit on and nothing to match, and was being offered a seat it could not use.
  if (bidder.isServerSideCompatible && hasNode && hasAdManagement) {
    options.push({
      value: 'adm-server-side',
      title: 'Server-side One (legacy)',
      description: 'The legacy server-side integration. Prefer the Server-side seat above for any new setup.',
      isDeprecated: true,
      trackingLabel: `${TRACKING_PREFIX}-adm-server-side-set`,
    })
  }

  // Spark Demand is deliberately absent. It is Pubstack selling its own demand, not something a scope
  // integrates, so it belongs to the internal flow that creates it (`CreateSeatFlow`'s `pbstck-bidder`)
  // rather than to the catalog. The method itself is kept — the seat still exists and is still created.
  return options
}

/**
 * The one option the internal Spark Demand flyout offers. It is deliberately not returned by
 * `seatSetupOptions`: the catalog is what a scope can integrate, and this is Pubstack putting its own
 * demand on someone else's adapter. Kept here so the copy lives beside its siblings.
 */
export const sparkDemandOption = (alreadyExists: boolean): SeatSetupOption => ({
  value: 'spark-demand',
  title: 'Spark Demand',
  description: 'Pubstack’s own demand on this adapter, mapped by placement. Internal — the customer never sees this seat.',
  disabledReason: alreadyExists ? SPARK_SEAT_ALREADY_CREATED : undefined,
  isSuggested: !alreadyExists,
  isAlreadyCreated: alreadyExists,
  isInternal: true,
  trackingLabel: `${TRACKING_PREFIX}-spark-demand-set`,
})

/** The catalog badge each method is advertised under. Both server-side seats share one badge. */
export const CARD_INTEGRATION_BY_METHOD: Record<SeatSetupMethod, CardIntegration> = {
  'client-side': 'Client-side',
  marketplace: 'Client-side',
  'adm-server-side': 'Server-side',
  'server-side': 'Server-side',
  'spark-for-pubs': 'Spark for Pubs',
  'spark-demand': 'Spark Demand',
}

/**
 * The offer that sells each setup method. One sells client-side bidding; everything a customer buys to
 * bid server-side is Node's.
 *
 * Read only where an option is actually for sale — the two Spark methods are not, and are gated by the
 * Spark team onboarding the scope rather than by anything on this map. Their entries are here because
 * the map is total, and are never the reason a card pitches an offer.
 */
const OFFER_BY_METHOD: Record<SeatSetupMethod, UpgradeOffer> = {
  'client-side': 'one',
  marketplace: 'one',
  'adm-server-side': 'node',
  'server-side': 'node',
  'spark-for-pubs': 'node',
  'spark-demand': 'node',
}

export type SeatCardAvailability = {
  /** Every integration the bidder supports, in card order — whether or not this scope can create it. */
  integrations: CardIntegration[]
  /** The subset the scope cannot create. Shown greyed, never dropped. */
  lockedIntegrations: CardIntegration[]
  /** Non-empty only when the scope can create nothing here: the offers that would unlock the bidder. */
  lockedOffers: UpgradeOffer[]
  /**
   * Why the card is wholly locked, when no offer sells it. In practice the two Spark methods: both are
   * enabled per scope by the Spark team, so pitching an offer would be the wrong sale — say "talk to us"
   * instead.
   */
  lockedReason?: string
}

/**
 * What a catalog card advertises: everything the **bidder** supports, with the seats this scope cannot
 * create marked rather than removed.
 *
 * The two axes stay separate, as they are in `seatSetupOptions`. What the bidder cannot do is not a
 * badge at all — an integration the adapter does not support is not a thing to sell. What the *scope*
 * has not bought is still a badge, greyed: dropping it turns "you need Node for this" into "Pubstack
 * does not do this", which is both wrong and the opposite of a cross-sell.
 *
 * `Server-side` is one badge over two methods, and counts as available if **either** is — a scope that
 * can create the current server-side seat can create a server-side seat, whatever the legacy one says.
 *
 * An already-created Spark seat is deliberately not considered: the card describes what the bidder can
 * be integrated as, not what is left to create.
 */
export const seatCardAvailability = (context: Omit<SeatSetupContext, 'sparkForPubAlreadyExists' | 'pbstckBidderSeatAlreadyExists'>): SeatCardAvailability => {
  const options = seatSetupOptions({ ...context, sparkForPubAlreadyExists: false, pbstckBidderSeatAlreadyExists: false })
  const integrations = [...new Set(options.map((option) => CARD_INTEGRATION_BY_METHOD[option.value]))]
  const available = new Set(options.filter((option) => !option.disabledReason).map((option) => CARD_INTEGRATION_BY_METHOD[option.value]))

  // Only when there was something to lock: a bidder that supports nothing at all is not an upsell, it is
  // simply a bidder with no integrations, and its card falls back to the item type.
  const noneAvailable = options.length > 0 && options.every((option) => option.disabledReason)

  // Asked of each option rather than of the bidder: `upgradeOffers` already draws the line this needs —
  // a list of offers is a lock someone can buy their way out of, an empty one is a lock to ask us about.
  // Reading `OFFER_BY_METHOD` off every option instead would sell Node for the two Spark methods, which
  // no amount of Node unlocks, and the card would pitch an upgrade that changes nothing.
  const lockedOffers = noneAvailable ? [...new Set(options.flatMap((option) => option.upgradeOffers ?? []))] : []

  return {
    integrations,
    lockedIntegrations: integrations.filter((integration) => !available.has(integration)),
    lockedOffers,
    // Nothing to sell, so say what would actually unlock it. Where some options are for sale and others
    // are not, the offers win: they are the part the reader can act on themselves.
    lockedReason: noneAvailable && lockedOffers.length === 0 ? options.find((option) => option.disabledReason)?.disabledReason : undefined,
  }
}

/**
 * The method a fresh form starts on: the suggested one when available, else the first available
 * non-deprecated option in card order. Undefined when the bidder supports nothing the scope is entitled to.
 */
export const defaultSeatSetupMethod = (options: SeatSetupOption[]): SeatSetupMethod | undefined => {
  const available = options.filter((option) => !option.disabledReason)
  return (available.find((option) => option.isSuggested) ?? available.find((option) => !option.isDeprecated))?.value
}

/** What a single mapping row is keyed by. Spark seats are mapped by placement and never ask. */
export type SeatMappingType = 'ad-unit' | 'device' | 'media-type' | 'device-media-type'

export const MAPPING_DIMENSIONS_BY_TYPE: Record<SeatMappingType, MappingDimensionKey[]> = {
  'ad-unit': ['siteId', 'adUnitId'],
  device: ['siteId', 'adUnitId', 'device'],
  'media-type': ['siteId', 'adUnitId', 'mediaType'],
  'device-media-type': ['siteId', 'adUnitId', 'device', 'mediaType'],
}

/**
 * How a mapping is dimensioned, in the words the add-a-seat flow uses.
 *
 * Exported so the edit flyout names the same two choices the same way: it used to phrase them as
 * "Mapping applies uniformly across all media types" and "Specific mapping per media type", which
 * described the same setting a reader had just chosen under a different name.
 */
export const MAPPING_TYPE_LABEL: Record<SeatMappingType, string> = {
  'ad-unit': 'One mapping per ad unit',
  device: 'One mapping per ad unit and device',
  'media-type': 'One mapping per ad unit and media type',
  'device-media-type': 'One mapping per ad unit, device and media type',
}

const isAdmMethod = (method: SeatSetupMethod) => SEAT_SPEC_BY_METHOD[method].type === 'adm'

/**
 * The two sides own `device` for opposite reasons, which is why no option list is shared.
 *
 * ADM **requires** it. `buildKleanadsDeviceSnapshot` builds the output once per device and keeps only
 * the mappings whose `dimensions.device` matches, so a mapping without the dimension matches neither
 * `desktop` nor `mobile` and is dropped from both — and those mappings are the sole source of the ad
 * unit's bidders. An ADM seat mapped per ad unit alone therefore serves nothing at all. Nothing
 * downstream catches it either: ADM creation returns before `validateMappingDimensions` runs, and the
 * schema accepts any dimension for any seat type. So ADM is offered `device` and `device`+media type,
 * never a bare choice that would leave the dimension off.
 *
 * PBS **cannot have it**: there the dimension is broken end to end — the CSV writer emits a Device
 * column the PBS row builder never fills, and the stack output keys mappings without it, so two
 * device rows collide and one is silently dropped.
 */
export const seatMappingTypeOptions = (method: SeatSetupMethod): { value: SeatMappingType; label: string; trackingLabel: TrackingLabel }[] =>
  (isAdmMethod(method) ? (['device', 'device-media-type'] as const) : (['ad-unit', 'media-type'] as const)).map((value) => ({
    value,
    label: MAPPING_TYPE_LABEL[value],
    trackingLabel: `${TRACKING_PREFIX}-mapping-${value}-set`,
  }))

/** Both defaults are the plainest option their side offers — and, for ADM, the one the pipeline needs. */
export const defaultSeatMappingType = (method: SeatSetupMethod): SeatMappingType => (isAdmMethod(method) ? 'device' : 'ad-unit')

export const seatMappingTypeLabel = (type: SeatMappingType): string => MAPPING_TYPE_LABEL[type]
